import React, { memo, useEffect, useMemo, useState } from 'react';
import { Typography, useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import { selectors as reactionSelectors } from '@app/store/reactions';
import { Post } from '@app/types/core';
import {
  accessingCurrentSpaceAsStaff,
  isInstructor,
} from '@core/selectors/classroom';
import { ANNOUNCEMENTS, COMMON_ROOM } from '@core/constants/postFeedType';
import { currentUserId } from '@core/selectors/user';
import { getComments } from '@core/selectors/comments';
import { AulaState } from '@core/types/state';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import formatDate from '@core/utils/formatDate';
import ResponsiveEditorReadOnly from '../ResponsiveEditorReadOnly';
import PostCardLayout from './PostCardLayout';
import PostReactions from './PostReactions';
import ResponsivePostTags from './ResponsivePostTags';
import ResponsivePostComments from './ResponsivePostComments';
import pushRoute from '@app/utils/routing';
import { getCurrentSpaceId } from '@core/selectors/space';
import { getCommonRoomPath } from '@app/actions/navigation';
import { MenuItemType, PostCardMenuItem } from './types';
import { MAKE_IMPORTANT, NOT_IMPORTANT } from '@app/constants/texts';
import { isScheduledPost } from '@core/utils/posts';
import EditPostModal from '../../CommonRoomResponsive/EditPostModal.container';
import { DEFAULT_ITEM_CLASS } from '../text';

type PostUser = Post['user'] & {
  avatar?: string;
  firstName?: string;
  lastName?: string;
};

type PostCardProps = {
  post: Post;
  isEducator: boolean;
  isAuthor: boolean;
  isReadOnlyUser: boolean;
  isUpdatingPost: boolean;
  navigateAfterDelete: boolean;
  isOpenMenuItemVisible?: boolean;
  handleDelete: (post: Post, navigateAfterDelete: boolean) => void;
  handleImportantPost: (post: Post) => void;
  handleOpenSchedulePost: (postId: string) => void;
  handlePublishPost: (postId: string) => void;
  addTag?: () => void;
  readOnly?: boolean;
};

const COMMON_ROOM_ACTIONS = {
  Open: 'open',
  Edit: 'edit',
  MakeImportant: 'makeImportant',
  Delete: 'delete',
};

type GetReactionsSelector = (
  state: AulaState,
  props: { itemId: string }
) => unknown[];

const parseContent = (content: string) => {
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const PostCard = ({
  post,
  isEducator,
  isAuthor,
  isReadOnlyUser,
  isUpdatingPost,
  handleDelete,
  handleImportantPost,
  readOnly = false,
}: PostCardProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));

  const author: PostUser = post.user;
  const getReactions = useMemo<GetReactionsSelector>(() => {
    const selector: any = reactionSelectors.getReactionsSelector();
    return (state, props) => selector(state, props);
  }, []);
  const hasReactions = useSelector<AulaState, boolean>(
    (state) => getReactions(state, { itemId: post.objectId }).length > 0
  );
  const hasComments = useSelector<AulaState, boolean>(
    (state) =>
      getComments(state, post.objectId, CommentsEntityTypes.post).length > 0
  );
  const { shouldShowTags } = useSelector((state: AulaState) => {
    const data = state.data as any;
    const storedPost = data.posts[post.objectId] || post;
    const hasTags = (storedPost.tagPosts || []).some(
      (tagPost: { tagId: string }) => data.tags[tagPost.tagId]
    );
    const canManageTags =
      !accessingCurrentSpaceAsStaff(state) &&
      (isInstructor(state) ||
        currentUserId(state) === storedPost.user.objectId);

    return {
      shouldShowTags: hasTags || canManageTags,
    };
  });
  const fullName =
    `${author.firstName || ''} ${author.lastName || ''}`.trim() ||
    author.email ||
    'Unknown user';

  const spaceId = useSelector(getCurrentSpaceId);
  const commonRoomPath = getCommonRoomPath(spaceId);

  // If the desktop edit dialog is open and the viewport becomes mobile, open the mobile edit page.
  useEffect(() => {
    if (isMobile && editDialogOpen && post?.objectId && spaceId) {
      pushRoute(`${commonRoomPath}/edit/${post?.objectId}`);
    }
  }, [isMobile, editDialogOpen, post?.objectId, spaceId]);

  const handleSelectMenuItem = (item: PostCardMenuItem) => {
    if (!isUpdatingPost) {
      switch (item?.key) {
        // TODO: Add open post functionality
        // case COMMON_ROOM_ACTIONS.Open:
        //   goToPost(post)();
        //   break;
        case COMMON_ROOM_ACTIONS.Edit:
          if (isMobile) {
            pushRoute(`${commonRoomPath}/edit/${post.objectId}`);
          } else {
            setEditDialogOpen(true);
          }
          break;
        case COMMON_ROOM_ACTIONS.MakeImportant:
          handleImportantPost(post);
          break;
        case COMMON_ROOM_ACTIONS.Delete:
          setDeleteDialogOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const menuItems: MenuItemType[] = [
    {
      key: COMMON_ROOM_ACTIONS.Open,
      getLabel: () => 'Open',
      isVisible: () => true,
    },
    {
      key: COMMON_ROOM_ACTIONS.Edit,
      getLabel: () => 'Edit',
      isVisible: ({ isAuthor: authorOfPost, isReadOnlyUser: readOnlyUser }) =>
        !readOnlyUser && authorOfPost,
    },
    {
      key: COMMON_ROOM_ACTIONS.MakeImportant,
      getLabel: ({ post: { important } }) =>
        !important ? MAKE_IMPORTANT : NOT_IMPORTANT,
      isVisible: ({ isEducator: educator, post: menuPost }) =>
        menuPost.feedType !== ANNOUNCEMENTS &&
        educator &&
        !isScheduledPost(menuPost),
    },
    {
      key: COMMON_ROOM_ACTIONS.Delete,
      getLabel: () => 'Delete',
      isVisible: ({
        isAuthor: authorOfPost,
        isEducator: educator,
        isReadOnlyUser: readOnlyUser,
      }) => !readOnlyUser && (authorOfPost || educator),
    },
  ];

  const menuContext = { isAuthor, isEducator, isReadOnlyUser, post };
  // Get all the current visible menu items for user
  const visibleMenuItems: PostCardMenuItem[] = menuItems
    ?.filter((item) => item.isVisible(menuContext))
    .map((item) => ({
      key: item.key,
      label: item.getLabel(menuContext),
    }));

  const editorState = useMemo(() => parseContent(post.content), [post.content]);
  const effectiveReadOnly = isReadOnlyUser || readOnly;
  const shouldShowReactions = !effectiveReadOnly || hasReactions;
  const shouldShowComments =
    post.feedType !== ANNOUNCEMENTS && (!effectiveReadOnly || hasComments);

  if (!post.content) return null;

  return (
    <>
      <PostCardLayout
        feedType={post?.feedType}
        ariaLabel={`Post by ${fullName}`}
        author={{
          name: fullName,
          avatarUrl: author.avatar,
          firstName: author.firstName,
          lastName: author.lastName,
          role: isEducator ? 'Educator' : undefined,
        }}
        timestamp={formatDate(post.scheduledFor || post.createdAt)}
        edited={Boolean(post.editedAt)}
        content={
          editorState ? (
            <ResponsiveEditorReadOnly
              key={post.editedAt || post.createdAt}
              itemId={post.objectId}
              itemType={post.feedType}
              editorState={editorState}
            />
          ) : (
            <Typography component="p" sx={{ m: 0 }}>
              {post.content}
            </Typography>
          )
        }
        tags={shouldShowTags ? <ResponsivePostTags post={post} /> : null}
        reactions={
          shouldShowReactions ? (
            <PostReactions
              postId={post.objectId}
              readOnly={effectiveReadOnly}
            />
          ) : null
        }
        comments={
          shouldShowComments ? (
            <ResponsivePostComments
              postId={post.objectId}
              readOnly={effectiveReadOnly}
            />
          ) : null
        }
        menuItems={visibleMenuItems}
        onSelectMenuItem={handleSelectMenuItem}
        deleteDialogOpen={deleteDialogOpen}
        onCloseDeleteDialog={() => setDeleteDialogOpen(false)}
        onConfirmDelete={() => handleDelete(post, false)}
        isLoading={isUpdatingPost}
        isPinnedPost={post?.important}
      />
      <EditPostModal
        key={post.objectId}
        open={editDialogOpen}
        content={post.content}
        itemClass={DEFAULT_ITEM_CLASS}
        itemId={post.objectId}
        itemType={post.feedType || COMMON_ROOM}
        onClose={() => setEditDialogOpen(false)}
      />
    </>
  );
};

export default memo(PostCard);
